import type { User } from '@/lib/auth-store';
import { resolveAdminPermissionKey } from '@/config/admin-permission-map';

export type PermissionKey =
  | 'analytics.view'
  | 'moderation.review'
  | 'moderation.manage'
  | 'finance.read'
  | 'finance.manage'
  | 'support.read'
  | 'support.manage'
  | 'content.manage'
  | 'security.read'
  | 'security.manage'
  | 'system.manage'
  /** Legacy Spatie names from admin auth API */
  | 'manage_settings'
  | 'manage_users'
  | 'manage_events'
  | 'manage_payments'
  | 'manage_subscriptions'
  | 'view_analytics'
  | 'view_kyc'
  | 'view_settlement_accounts'
  | 'view_organizer_requests'
  | 'view_finance_dashboard'
  | 'view_settlements'
  | 'view_refunds'
  | 'view_event_governance'
  | 'view_user_governance'
  | 'view_admin_teams'
  | 'view_team_assignments'
  | 'view_cases'
  | 'view_legal_consents'
  | 'view_user_conversations'
  | 'davs.view'
  | 'davs.manage'
  | 'davs.document.manage'
  | 'davs.document.approve'
  | 'davs.document.publish'
  | 'davs.template.manage'
  | 'davs.letterhead.manage'
  | 'davs.trust.view'
  | 'davs.audit.view';

export type PermissionRequirement =
  | PermissionKey
  | PermissionKey[]
  | {
      anyOf?: PermissionKey[];
      allOf?: PermissionKey[];
    };

function normalizePermissions(user: User | null | undefined): Set<string> {
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return new Set(permissions.map((permission) => String(permission).trim().toLowerCase()));
}

export function hasPermission(
  user: User | null | undefined,
  requirement?: PermissionRequirement
): boolean {
  if (!requirement) return true;

  if (user) {
    const role = String(user.role || user.user_type || '').toLowerCase();
    if (
      role === 'admin' ||
      role === 'super_admin' ||
      role === 'super-admin' ||
      user.super_platform_access === true ||
      user.master_admin === true ||
      (Array.isArray(user.roles) &&
        user.roles.some((r: any) => {
          const name = typeof r === 'string' ? r : r?.name;
          return ['admin', 'super_admin', 'super-admin', 'system_admin', 'platform_admin'].includes(
            String(name || '').toLowerCase()
          );
        }))
    ) {
      return true;
    }
  }

  const permissionSet = normalizePermissions(user);
  if (permissionSet.size === 0) return false;

  const hasSingle = (permission: PermissionKey): boolean => {
    const resolved = resolveAdminPermissionKey(permission);
    const needle = resolved.toLowerCase();
    const original = permission.toLowerCase();
    if (permissionSet.has(needle) || permissionSet.has(original)) return true;
    // davs.manage is a blanket grant for all DAVS nav/API surfaces.
    if ((needle.startsWith('davs.') || original.startsWith('davs.')) && permissionSet.has('davs.manage')) {
      return true;
    }
    return false;
  };

  if (typeof requirement === 'string') {
    return hasSingle(requirement);
  }

  if (Array.isArray(requirement)) {
    return requirement.every(hasSingle);
  }

  const anyOf = requirement.anyOf ?? [];
  const allOf = requirement.allOf ?? [];
  const anyPass = anyOf.length === 0 || anyOf.some(hasSingle);
  const allPass = allOf.every(hasSingle);
  return anyPass && allPass;
}
